import { type AbortController } from "@azure/abort-controller";
import { TaskStatuses } from "enums";
import TaskActions from "enums/TaskActions";
import backgroundTasksEventsEmitter from "features/BackgroundTasks/events/backgroundTasksEventsEmitter";
import { type VideoFeedbackPageTask } from "features/BackgroundTasks/taskPool";
import fileUtils from "utils/fileUtils";
import taskPool from "utils/taskPool";
import campaignsDataService from "./campaignsDataService";

type TaskActionsType = {
  addTask: (task: VideoFeedbackPageTask) => void;
  updateTask: (id: number | string, task: VideoFeedbackPageTask) => void;
  deleteTask: (id: number | string) => void;
  updateProgress: (id: number, percent: number, indeterminate: boolean) => void;
};

export interface IFeedbackPageVideoUploaderService {
  uploadFilesHandler: (
    campaignId: number,
    backgroundTasksActions: TaskActionsType,
    files: FileList,
    setVideoUploadTaskId: (value: string) => void,
  ) => Promise<string>;
  cancelFileUploading: (backgroundTasksActions: TaskActionsType, id: string) => void;
}

let aborter: AbortController;
let taskId: string;
let tasksActions: TaskActionsType;

const acceptUploadAborter = (abortedHandler: AbortController) => {
  aborter = abortedHandler;
  return aborter;
};

const onUploadProgressHandler = (progressEvent: { loaded: number; total: number }) => {
  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
  tasksActions.updateProgress(Number(taskId), percentCompleted, false);
  return percentCompleted;
};

const videoUploaderService: IFeedbackPageVideoUploaderService = {
  uploadFilesHandler(
    campaignId: number,
    backgroundTasksActions: TaskActionsType,
    files: FileList,
    setVideoUploadTaskId: (value: string) => void,
  ): Promise<string> {
    const uploadFileHandler = async (file: File): Promise<string> => {
      tasksActions = backgroundTasksActions;
      taskId = file.name + Date.now();
      const task: VideoFeedbackPageTask = {
        id: taskId,
        status: TaskStatuses.queued,
        state: TaskStatuses.queued,
        title: file.name,
        label: "Uploading...",
        errorLabel: "File upload failed!",
        canClose: true,
        percent: 0,
        isFeedbackPageVideoUploading: true,
        isFeedbackPageVideoUploaded: false,
        indeterminate: false,
        onCancel: () => {
          backgroundTasksActions.deleteTask(taskId);
        },
        operation: {
          messages: [],
          succeeded: [],
          failed: [],
        },
      };

      const getStarted = (videoUploadTask: VideoFeedbackPageTask) => {
        let startedTask: VideoFeedbackPageTask = {
          ...videoUploadTask,
          status: TaskStatuses.inProgress,
          isFeedbackPageVideoUploading: true,
        };

        return startedTask;
      };

      const getUploadedSuccessfuly = (videoUploadTask: VideoFeedbackPageTask) => {
        let uploadedTask: VideoFeedbackPageTask = {
          ...videoUploadTask,
          label: "Uploaded successfully!",
          errorLabel: "Create failed!",
          percent: 100,
          status: TaskStatuses.successful,
          isFeedbackPageVideoUploading: false,
          isFeedbackPageVideoUploaded: true,
        };
        return uploadedTask;
      };

      const getFailed = (videoUploadTask: VideoFeedbackPageTask) => {
        let failedTask: VideoFeedbackPageTask = {
          ...videoUploadTask,
          status: TaskStatuses.failed,
          errorLabel: "Video processing failed!",
          percent: 100,
          indeterminate: false,
        };
        return failedTask;
      };

      try {
        backgroundTasksActions.addTask(task);
        backgroundTasksActions.updateTask(taskId, getStarted(task));
        setVideoUploadTaskId(taskId);

        backgroundTasksEventsEmitter.subscribeOnce(TaskActions.cancel, task.id, taskPool.cancelTask.bind(taskPool));

        let uploadOptions = { contentType: file.type };
        let sasTokenUri = await campaignsDataService.getFeedbackPageContentUrlWithSasAsync(campaignId);
        await fileUtils.uploadFileToBlob(
          sasTokenUri,
          file,
          onUploadProgressHandler,
          acceptUploadAborter,
          uploadOptions,
        );

        backgroundTasksActions.updateTask(taskId, getUploadedSuccessfuly(task));
        return sasTokenUri;
      } catch (error: any) {
        if (error.code === "REQUEST_ABORTED_ERROR") {
          return "";
        }
        backgroundTasksActions.updateTask(taskId, getFailed(task));
        return "";
      }
    };

    return uploadFileHandler(files[0]);
  },

  cancelFileUploading(backgroundTasksActions: TaskActionsType, id: string) {
    if (!aborter || aborter.signal.aborted) {
      return;
    }

    aborter.abort();
    backgroundTasksActions.deleteTask(id);
  },
};

export default videoUploaderService;
